import { forwardRef, RefObject, useCallback } from 'react'

import { Post } from '@tribeplatform/gql-client/types'

import { ReplyBar } from '../Post/ReplyBar.js'
import { PostContext } from '../Post/types.js'

import { REPLY_LOAD_MORE_REPLIES_LIMIT } from './constants.js'
import { GenericReply } from './GenericReply.js'
import { useGenericReply } from './hooks/useGenericReply.js'

interface Props {
  reply: Post
  canReply: boolean
  readonly?: boolean
  context?: PostContext
  post?: Post
  isHighlighted?: boolean
  nestLevel?: number
  scrollToElement?: (props: { elementRef?: RefObject<HTMLElement> }) => void
  onEdit?: (replyId: string) => void
}

export const Reply = forwardRef<HTMLDivElement, Props>(
  (
    {
      reply,
      canReply,
      readonly = false,
      context,
      post,
      isHighlighted = false,
      nestLevel = 0,
      scrollToElement,
      onEdit,
    },
    ref,
  ) => {
    const {
      isFetching,
      unloadedReplies,
      replies,
      showReplyBar,
      editing,
      replyBarRef,
      isHidden,
      hasReplyPermission,
      fetchNextPage,
      onReply,
      stopEditing,
      startEditing,
      refetchPageContainsId,
    } = useGenericReply({
      limit: REPLY_LOAD_MORE_REPLIES_LIMIT,
      reply,
    })

    const handleOnStopEditing = useCallback(() => {
      stopEditing()
      onEdit?.(reply.id)
    }, [stopEditing, onEdit, reply.id])

    if (isHidden) {
      return null
    }

    return (
      <GenericReply
        ref={ref}
        context={context}
        post={post}
        reply={reply}
        canReply={canReply && hasReplyPermission}
        isFetching={isFetching}
        onLoadMore={fetchNextPage}
        editing={editing}
        onStopEditing={handleOnStopEditing}
        onStartEditing={startEditing}
        onReply={onReply}
        readonly={readonly}
        isHighlighted={isHighlighted}
        unloadedReplies={unloadedReplies}
        nestLevel={nestLevel}
      >
        {replies?.length > 0 || (canReply && showReplyBar) ? (
          <div className="relative flex flex-col space-y-6 mt-6 p-2 ps-0">
            {replies
              ?.map(reply => (
                <Reply
                  onEdit={refetchPageContainsId}
                  reply={reply}
                  canReply={canReply}
                  readonly={readonly}
                  key={reply.id}
                  // If nestLevel is zero, it means that we are not manually handling the indentation
                  // This happens in highlighted replies
                  nestLevel={nestLevel + 1}
                  scrollToElement={scrollToElement}
                />
              ))
              ?.reverse()}
            {canReply && showReplyBar && (
              <ReplyBar
                ref={replyBarRef}
                post={reply}
                defaultExpand
                scrollToElement={scrollToElement}
              />
            )}
          </div>
        ) : null}
      </GenericReply>
    )
  },
)
